import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { img } from "@/lib/format";

export default function CartDrawer() {
  const {
    cart, updateQty, removeFromCart, subtotalInr, fmt, drawerOpen, setDrawerOpen,
    config, cartCount, user, wallet, refreshWallet,
  } = useStore();
  useEffect(() => {
    if (drawerOpen && user) refreshWallet();
  }, [drawerOpen, user, refreshWallet]);
  const threshold = config.free_shipping_threshold || 2999;
  const progress = Math.min(subtotalInr / threshold, 1);
  const freeShip = subtotalInr >= threshold;

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            data-testid="cart-drawer-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-md flex-col bg-alabaster shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-linec px-6 py-5">
              <h3 className="font-serif text-2xl">Your Bag ({cartCount})</h3>
              <button data-testid="cart-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close cart" className="p-1 transition-colors duration-200 hover:text-crimson">
                <X size={22} />
              </button>
            </div>

            {cart.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center" data-testid="cart-drawer-empty">
                <ShoppingBag size={40} strokeWidth={1.2} className="text-linec" />
                <p className="text-sm text-mutedc">Your bag is empty — for now.</p>
                <Link
                  to="/shop" data-testid="cart-drawer-shop-link" onClick={() => setDrawerOpen(false)}
                  className="rounded-full bg-crimson px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson-dark"
                >
                  Shop the Collection
                </Link>
              </div>
            ) : (
              <>
                <div className="border-b border-linec px-6 py-4">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-mutedc">
                    {freeShip ? "You've unlocked free express shipping" : `Add ${fmt(threshold - subtotalInr)} more for free shipping`}
                  </p>
                  <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-linec">
                    <div
                      data-testid="cart-drawer-free-ship-progress"
                      className="h-full rounded-full bg-crimson transition-all duration-500"
                      style={{ width: `${progress * 100}%` }}
                    />
                  </div>
                </div>

                <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
                  {cart.map((item) => (
                    <div key={`${item.slug}:${item.size}`} data-testid={`cart-drawer-item-${item.slug}`} className="flex gap-4">
                      <img src={img(item.image, 200)} alt={item.name} className="h-24 w-20 flex-shrink-0 object-cover" />
                      <div className="flex flex-1 flex-col justify-between">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold leading-snug">{item.name}</p>
                            <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-mutedc">Size {item.size}</p>
                          </div>
                          <button data-testid={`cart-drawer-remove-${item.slug}`} onClick={() => removeFromCart(item.slug, item.size)} aria-label="Remove" className="p-1 text-mutedc transition-colors duration-200 hover:text-crimson">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-linec">
                            <button data-testid={`cart-drawer-qty-minus-${item.slug}`} onClick={() => updateQty(item.slug, item.size, item.qty - 1)} className="p-1.5 px-2 transition-colors hover:text-crimson" aria-label="Decrease"><Minus size={13} /></button>
                            <span data-testid={`cart-drawer-qty-${item.slug}`} className="w-7 text-center font-mono text-xs">{item.qty}</span>
                            <button data-testid={`cart-drawer-qty-plus-${item.slug}`} onClick={() => updateQty(item.slug, item.size, item.qty + 1)} className="p-1.5 px-2 transition-colors hover:text-crimson" aria-label="Increase"><Plus size={13} /></button>
                          </div>
                          <p className="text-sm font-semibold">{fmt(item.price_inr * item.qty, item.price_usd * item.qty)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4 border-t border-linec px-6 py-5">
                  {user && wallet?.balance > 0 && (
                    <Link to="/checkout" data-testid="cart-wallet-nudge" onClick={() => setDrawerOpen(false)}
                      className="flex items-center justify-between border border-crimson bg-crimson-sub px-4 py-3 transition-colors duration-200 hover:border-crimson-dark">
                      <span className="flex items-center gap-2 text-sm font-semibold text-crimson">
                        <Wallet size={15} /> {fmt(wallet.balance)} cashback waiting
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-crimson">Redeem at checkout</span>
                    </Link>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-mutedc">Subtotal</span>
                    <span data-testid="cart-drawer-subtotal" className="font-serif text-2xl">{fmt(subtotalInr)}</span>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Shipping & taxes calculated at checkout</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Link
                      to="/cart" data-testid="cart-drawer-view-cart" onClick={() => setDrawerOpen(false)}
                      className="flex items-center justify-center rounded-full border border-ink px-4 py-3 text-sm font-semibold transition-colors duration-200 hover:bg-ink hover:text-white"
                    >
                      View Cart
                    </Link>
                    <Link
                      to="/checkout" data-testid="cart-drawer-checkout" onClick={() => setDrawerOpen(false)}
                      className="flex items-center justify-center rounded-full bg-crimson px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-crimson-dark"
                    >
                      Checkout
                    </Link>
                  </div>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
